import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { renderSVG } from 'uqr';
import { Copy, KeyRound, ShieldCheck, ShieldOff } from 'lucide-react';
import type { TotpEnabled, TotpSetup } from '@mnc/contracts';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Badge, Field, PageHeader } from '../components/ui';

/** My account: the password, and the second factor (required for some groups on central, D2.3; optional otherwise). */
export function AccountPage() {
  const { t } = useTranslation();
  const { me, refresh } = useAuth();
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const codeError = (e: unknown) => setError(e instanceof ApiError && e.code === 'totp_invalid' ? t('account.codeInvalid') : e instanceof ApiError ? e.message : t('errors.generic'));
  const start = useMutation({ mutationFn: () => api.post<TotpSetup>('/api/me/totp/setup'), onSuccess: (s) => { setSetup(s); setCode(''); setError(null); }, onError: codeError });
  const enable = useMutation({ mutationFn: () => api.post<TotpEnabled>('/api/me/totp/enable', { code }), onSuccess: async (r) => { toast.success(t('account.enabled')); setSetup(null); setCode(''); setCodes(r.recovery_codes); await refresh(); }, onError: codeError });
  const disable = useMutation({ mutationFn: () => api.post('/api/me/totp/disable', { code }), onSuccess: async () => { toast.success(t('account.disabled')); setCode(''); setCodes(null); await refresh(); }, onError: codeError });
  const regenerate = useMutation({ mutationFn: () => api.post<TotpEnabled>('/api/me/totp/recovery-codes', { code }), onSuccess: async (r) => { setCode(''); setError(null); setCodes(r.recovery_codes); await refresh(); }, onError: codeError });
  if (!me) return null;
  const required = me.totp_required;

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader title={t('account.title')} subtitle={me.email} />
      {required && <p className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-200" role="alert">{t('account.requiredNotice')}</p>}
      <div className="grid gap-4">
        {me.auth_provider === 'local' && <section className="card p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="flex items-center gap-2 font-semibold">{me.totp_enabled ? <ShieldCheck size={18} className="text-brand-600" /> : <ShieldOff size={18} className="text-neutral-400" />} {t('account.totpTitle')}</h2>
            <Badge tone={me.totp_enabled ? 'brand' : 'neutral'}>{me.totp_enabled ? t('account.on') : t('account.off')}</Badge>
          </div>
          <p className="mt-1 text-sm text-neutral-500">{t('account.totpHint')}</p>
          {!me.totp_enabled && !setup && <button type="button" className="btn-primary mt-3" disabled={start.isPending} onClick={() => start.mutate()}>{t('account.setUp')}</button>}
          {!me.totp_enabled && setup && (
            <div className="mt-3 grid gap-3 sm:grid-cols-[minmax(0,12rem)_minmax(0,1fr)]">
              <div className="rounded-lg bg-white p-2 dark:bg-neutral-100" dangerouslySetInnerHTML={{ __html: renderSVG(setup.otpauth_url, { border: 1 }) }} aria-label={t('account.qr')} />
              <div className="grid content-start gap-2">
                <p className="text-sm">{t('account.scanHint')}</p>
                <p className="text-xs text-neutral-500">{t('account.secretHint')} <code className="select-all rounded bg-neutral-100 px-1 py-0.5 dark:bg-neutral-800" dir="ltr">{setup.secret}</code></p>
                <Field label={t('account.code')} required>
                  <input className="input w-40" inputMode="numeric" pattern="\\d{6}" maxLength={6} dir="ltr" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
                </Field>
                {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
                <div className="flex gap-2">
                  <button type="button" className="btn-primary" disabled={enable.isPending || code.length !== 6} onClick={() => enable.mutate()}>{t('account.confirm')}</button>
                  <button type="button" className="btn-secondary" onClick={() => { setSetup(null); setError(null); }}>{t('app.cancel')}</button>
                </div>
              </div>
            </div>
          )}
          {me.totp_enabled && codes && (
            <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-950/40" role="alert">
              <p className="text-sm font-medium">{t('account.recoveryTitle')}</p>
              <p className="mt-1 text-xs text-neutral-600 dark:text-neutral-300">{t('account.recoveryHint')}</p>
              <ul className="mt-2 grid grid-cols-2 gap-1 font-mono text-sm sm:grid-cols-4" dir="ltr">
                {codes.map((c) => <li key={c} className="select-all rounded bg-white px-2 py-1 dark:bg-neutral-900">{c}</li>)}
              </ul>
              <div className="mt-2 flex flex-wrap gap-2">
                <button type="button" className="btn-secondary" onClick={() => { void navigator.clipboard.writeText(codes.join('\n')); toast.success(t('app.copied')); }}><Copy size={16} /> {t('account.copyCodes')}</button>
                <button type="button" className="btn-primary" onClick={() => setCodes(null)}>{t('account.savedCodes')}</button>
              </div>
            </div>
          )}
          {me.totp_enabled && !codes && (
            <div className="mt-3 grid gap-2">
              <p className="text-xs text-neutral-500">{t('account.recoveryRemaining', { count: me.totp_recovery_remaining })} {required || me.groups.length === 0 ? '' : t('account.removeHint')}</p>
              <div className="flex flex-wrap items-end gap-2">
                <Field label={t('account.code')}>
                  <input className="input w-40" inputMode="numeric" maxLength={6} dir="ltr" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
                </Field>
                <button type="button" className="btn-secondary" disabled={regenerate.isPending || code.length !== 6} onClick={() => regenerate.mutate()}>{t('account.newCodes')}</button>
                {!required && <button type="button" className="btn-danger" disabled={disable.isPending || code.length !== 6} onClick={() => disable.mutate()}>{t('account.remove')}</button>}
              </div>
              {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
            </div>
          )}
        </section>}
        {me.auth_provider === 'local' && (
          <section className="card p-4">
            <h2 className="flex items-center gap-2 font-semibold"><KeyRound size={18} /> {t('password.changeTitle')}</h2>
            <Link to="/change-password" className="btn-secondary mt-3">{t('account.changePassword')}</Link>
          </section>
        )}
        {me.auth_provider === 'central' && <p className="text-sm text-neutral-500">{t('account.centralAccount')}</p>}
      </div>
    </div>
  );
}
